// ** Third Party Components
import { useLocation, useNavigate } from "react-router-dom"

// ** helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useCommonStore } from "@pma/stores/common-store"

// ** Types
import { NavBarLinkProps } from "@pma/types"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

const NavBarLink = ({
  item,
  path,
  title,
  disabled,
  externalLink,
  openInNewTab,
  onClick,
  isActiveOverride,
}: NavBarLinkProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { translate } = useTranslator()
  const { setHeaderBreadcrumbsListAction } = useCommonStore()

  const isActive =
    isActiveOverride ??
    (!!path && (location.pathname === path || location.pathname.startsWith(`${path}/`)))

  const linkClasses = cn(
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-[inter] transition-colors cursor-pointer",
    disabled
      ? "text-gray-400 cursor-not-allowed"
      : isActive
        ? "bg-indigo-200 font-bold text-neutral-700"
        : "hover:bg-indigo-200 font-normal text-[#25272D]",
  )

  // Toggle items: fire the toggle first, then navigate if a path is provided.
  // This keeps Project/Files consistent — clicking the icon both reveals the
  // dropdown AND opens the corresponding page.
  if (onClick) {
    const handleToggleClick = () => {
      onClick()
      if (path && location.pathname !== path) {
        if (item?.breadcrumbs) {
          localStorage.setItem("breadcrumbs", JSON.stringify(item.breadcrumbs))
          setHeaderBreadcrumbsListAction(item.breadcrumbs)
        }
        navigate(path)
      }
    }
    return (
      <div className={linkClasses} onClick={handleToggleClick}>
        {item?.icon ? <item.icon variant={isActive ? "Bold" : "Linear"} /> : null}
        {translate(title)}
      </div>
    )
  }

  if (!path) {
    return (
      <div className={linkClasses}>
        {item?.icon ? <item.icon variant={isActive ? "Bold" : "Linear"} /> : null}
        {translate(title)}
      </div>
    )
  }

  if (externalLink) {
    return (
      <a
        href={path}
        target={openInNewTab ? "_blank" : "_self"}
        rel='noopener noreferrer'
        className={linkClasses}
      >
        {item?.icon ? <item.icon variant={isActive ? "Bold" : "Linear"} /> : null}
        {translate(title)}
      </a>
    )
  }

  const handleSetBreadcrumbs = () => {
    if (location.pathname !== path) {
      localStorage.setItem("breadcrumbs", JSON.stringify(item.breadcrumbs))
      setHeaderBreadcrumbsListAction(item.breadcrumbs)
      navigate(path)
    }
  }

  return (
    <div className={linkClasses} onClick={handleSetBreadcrumbs}>
      {item?.icon ? <item.icon variant={isActive ? "Bold" : "Linear"} /> : null}
      {translate(title)}
    </div>
  )
}

export default NavBarLink
